import React, { useCallback, useMemo, useRef, useState } from 'react';
import { styled } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import Button, { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/close';
import Image from '@atlaskit/image';
import Modal, { ModalTransition, type OnCloseHandler } from '@atlaskit/modal-dialog';
import { Box, Grid, Stack, xcss } from '@atlaskit/primitives';
import { ProgressIndicator } from '@atlaskit/progress-indicator';
import { token } from '@atlaskit/tokens';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';

import type { WhiteboardOnboardingDialogListConfiguration } from './WhiteboardInternalModal';

export const i18n = defineMessages({
	closeButtonLabel: {
		id: 'whiteboard-common.onboarding-modal.closeButtonLabel',
		defaultMessage: 'Close modal',
		description:
			"ARIA label for a button with a 'X' icon in a modal header letting the user know that clicking it will close the modal.",
	},
	prevButtonLabel: {
		id: 'whiteboard-common.onboarding-modal.prevButtonLabel',
		defaultMessage: 'Previous page',
		description:
			"ARIA label for a button with a '<' icon in a modal letting the user know that clicking it will go to the previous page.",
	},
	nextButtonLabel: {
		id: 'whiteboard-common.onboarding-modal.nextButtonLabel',
		defaultMessage: 'Next page',
		description:
			"ARIA label for a button with a '>' icon in a modal letting the user know that clicking it will go to the next page.",
	},
});

const gridStyles = xcss({
	gridTemplateColumns: '1fr',
});

const closeContainerStyles = xcss({
	gridRowStart: '1',
	gridColumnStart: '1',
	padding: 'space.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OverlayedButton = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	borderRadius: '50%',
	backgroundColor: token('color.blanket'),
	cursor: 'pointer',
	padding: 0,
	opacity: 0.9,
	width: '27px',
	height: '27px',
	pointerEvents: 'auto',
	userSelect: 'none',
	'&:hover': {
		opacity: 0.99,
	},
});

const chevronContainerStyles = xcss({
	gridRowStart: '1',
	gridColumnStart: '1',
	padding: 'space.200',
	alignItems: 'center',
});

const imageContainerStyles = xcss({
	gridRowStart: '1',
	gridColumnStart: '1',
});

const contentContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
});

const bodyContainerStyles = xcss({
	paddingBlockStart: 'space.400',
	paddingBlockEnd: 'space.200',
	paddingInline: 'space.300',
	alignItems: 'center',
});

const bodyTextContainerStyles = xcss({
	width: '88%',
	paddingBlockStart: 'space.100',
	textAlign: 'center',
});

const footerStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	alignItems: 'center',
	paddingBlockStart: 'space.100',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	paddingBottom: 'space.300',
});

const footerElementContainerStyles = xcss({
	flexGrow: 1,
	flexBasis: 0,
});

const rightFooterElementContainerStyles = xcss({
	flexGrow: 1,
	flexBasis: 0,
	display: 'flex',
	justifyContent: 'flex-end',
});

export interface WhiteboardOnboardingSlidesModalProps
	extends WhiteboardOnboardingDialogListConfiguration {
	closeModal: () => void;
}

type SvgCache = {
	[key: string]: string;
};

export const WhiteboardInternalOnboardingSlidesModal: React.FC<
	WhiteboardOnboardingSlidesModalProps
> = ({
	dialogList,
	closeModal,
	bodyTextHeight,
	props: dialogProps,
}: WhiteboardOnboardingSlidesModalProps) => {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const svgCache = useRef<SvgCache>({});
	const { formatMessage } = useIntl();

	const {
		titleContent,
		titleProps,
		bodyContent,
		bodyProps,
		skipButton,
		nextOrDoneButton,
		svgImage,
	} = useMemo(() => {
		const {
			modalHeader: {
				title: { content: titleContent, props: titleProps },
			},
			modalBody: { content: bodyContent, props: bodyProps },
			modalFooter: {
				buttons: [skipButton, nextOrDoneButton],
			},
			image: svgImage,
		} = dialogList[selectedIndex];

		// Cache the SVG if not already cached
		if (svgImage && !svgCache.current[svgImage]) {
			svgCache.current[svgImage] = svgImage;
		}

		return {
			titleContent,
			titleProps,
			bodyContent,
			bodyProps,
			skipButton,
			nextOrDoneButton,
			svgImage: svgImage ? svgCache.current[svgImage] : '',
		};
	}, [dialogList, selectedIndex]);

	const onModalClose: OnCloseHandler = useCallback(
		(e, analyticsEvent) => {
			dialogProps?.onClose?.(e, analyticsEvent);
			closeModal();
		},
		[closeModal, dialogProps],
	);

	const handlePrev = useCallback(() => {
		setSelectedIndex((prevState) => prevState - 1);
	}, []);

	const handleNext = useCallback(() => {
		setSelectedIndex((prevState) => prevState + 1);
	}, []);

	const handleNextOrDoneButtonClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>, analyticsEvent: UIAnalyticsEvent) => {
			if (selectedIndex === dialogList.length - 1) {
				nextOrDoneButton.props?.onClick?.(e, analyticsEvent);
				onModalClose(e, analyticsEvent);
				return;
			}
			handleNext();
		},
		[handleNext, onModalClose, selectedIndex, dialogList, nextOrDoneButton],
	);

	return (
		<>
			<ModalTransition>
				<Modal {...dialogProps} onClose={onModalClose} shouldScrollInViewport>
					<Grid gap="space.0" xcss={gridStyles}>
						<Grid xcss={closeContainerStyles} justifyContent="end">
							<IconButton
								testId={`${dialogProps?.testId}-close-button`}
								appearance="subtle"
								icon={CrossIcon}
								label={formatMessage(i18n.closeButtonLabel)}
								onClick={onModalClose}
							/>
						</Grid>
						{selectedIndex !== 0 && (
							<Grid xcss={chevronContainerStyles} justifyContent="start">
								<OverlayedButton
									data-testid={`${dialogProps?.testId}-left-chevron-button`}
									onClick={handlePrev}
								>
									<ChevronLeftIcon
										color={token('color.icon.inverse')}
										label={formatMessage(i18n.prevButtonLabel)}
										size="small"
									/>
								</OverlayedButton>
							</Grid>
						)}
						{selectedIndex !== dialogList.length - 1 && (
							<Grid xcss={chevronContainerStyles} justifyContent="end">
								<OverlayedButton
									data-testid={`${dialogProps?.testId}-right-chevron-button`}
									onClick={handleNext}
								>
									<ChevronRightIcon
										color={token('color.icon.inverse')}
										label={formatMessage(i18n.nextButtonLabel)}
										size="small"
									/>
								</OverlayedButton>
							</Grid>
						)}
						<Grid xcss={imageContainerStyles} justifyContent="start">
							<Image testId={`${dialogProps?.testId}-image`} src={svgImage} alt="" />
						</Grid>
					</Grid>
					<Box xcss={contentContainerStyles}>
						<Stack xcss={bodyContainerStyles}>
							<Heading {...titleProps} as="h1" size="medium">
								{titleContent}
							</Heading>
							<Box
								{...bodyProps}
								xcss={bodyTextContainerStyles}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
								style={{ minHeight: bodyTextHeight ?? '88px' }}
								as="p"
								dangerouslySetInnerHTML={{ __html: bodyContent }}
							/>
						</Stack>
						<Box xcss={footerStyles}>
							<Box xcss={footerElementContainerStyles}>
								<Button {...skipButton.props} onClick={onModalClose} appearance="subtle">
									{skipButton.label}
								</Button>
							</Box>
							<Box xcss={footerElementContainerStyles}>
								<ProgressIndicator
									testId={`${dialogProps?.testId}-progress-indicator`}
									size="default"
									appearance="help"
									selectedIndex={selectedIndex}
									values={dialogList}
								/>
							</Box>
							<Box xcss={rightFooterElementContainerStyles}>
								<Button
									{...nextOrDoneButton.props}
									onClick={handleNextOrDoneButtonClick}
									appearance="discovery"
								>
									{nextOrDoneButton.label}
								</Button>
							</Box>
						</Box>
					</Box>
				</Modal>
			</ModalTransition>
		</>
	);
};
